import { useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router';
import {
  BookkeepingEntry,
  entryStatus,
  useEntryQuery,
  useCreateEntryMutation,
  useCreateProductMixtureEntryMutation,
  useUpdateEntryMutation,
  useUpdateApprovedEntryNotesMutation,
  useDeleteEntryMutation,
  useEntriesQuery,
} from '@api/queries/inventory';
import { EntryType } from '~types/types';

interface LocationState {
  returnSearch?: string;
}

// ============================================================================
// Types
// ============================================================================

export type EditorMode = 'new' | 'edit' | 'approved' | 'readonly';

export interface UseBookkeepingEntryOptions {
  entryId?: string;
  isNew: boolean;
}

export interface UseBookkeepingEntryResult {
  // Entry data
  entry: BookkeepingEntry | undefined;
  isLoading: boolean;
  isError: boolean;

  // Mode
  mode: EditorMode;

  // Mutations
  saveEntry: (entry: BookkeepingEntry) => Promise<void>;
  deleteEntry: () => Promise<void>;
  isSaving: boolean;
  isDeleting: boolean;

  // Navigation
  close: () => void;
  editNextUnconfirmed: () => void;
  hasNextUnconfirmed: boolean;
}

// ============================================================================
// Hook
// ============================================================================

export const useBookkeepingEntry = ({
  entryId,
  isNew,
}: UseBookkeepingEntryOptions): UseBookkeepingEntryResult => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get the return search params from navigation state (if any)
  const returnSearch = (location.state as LocationState)?.returnSearch || '';
  const returnUrl = `/bookkeeping${returnSearch}`;

  // Fetch entry if editing
  const {
    data: entry,
    isLoading: isLoadingEntry,
    isError,
  } = useEntryQuery(isNew ? undefined : entryId);

  // Fetch tank entries for "next unconfirmed" feature
  const tankId = entry?.tankId;
  const { data: tankEntries = [] } = useEntriesQuery(
    { tankId },
    !!tankId && !isNew
  );

  // Mutations
  const createEntryMutation = useCreateEntryMutation();
  const createMixtureMutation = useCreateProductMixtureEntryMutation();
  const updateEntryMutation = useUpdateEntryMutation();
  const updateApprovedNotesMutation = useUpdateApprovedEntryNotesMutation();
  const deleteEntryMutation = useDeleteEntryMutation();

  // Determine mode
  const mode: EditorMode = useMemo(() => {
    if (isNew) return 'new';
    if (!entry) return 'edit'; // Still loading, default to edit
    if (entry.entryState === entryStatus.approved) return 'approved';
    return 'edit';
  }, [isNew, entry]);

  // Find next unconfirmed entry in same tank
  const nextUnconfirmed = useMemo(() => {
    if (!entry || !tankEntries.length) return null;

    const currentIndex = tankEntries.findIndex((e) => e.id === entryId);
    if (currentIndex < 0) return null;

    return tankEntries.find(
      (e, i) => i > currentIndex && e.entryState === entryStatus.incomplete
    ) || null;
  }, [entry, tankEntries, entryId]);

  // Loading state
  const isLoading = !isNew && isLoadingEntry;

  // Saving state
  const isSaving =
    createEntryMutation.isPending ||
    createMixtureMutation.isPending ||
    updateEntryMutation.isPending ||
    updateApprovedNotesMutation.isPending;

  const isDeleting = deleteEntryMutation.isPending;

  // Save handler
  const saveEntry = useCallback(
    async (entryData: BookkeepingEntry) => {
      try {
        if (mode === 'new') {
          if (entryData.entryType === EntryType.Mixture) {
            await createMixtureMutation.mutateAsync(entryData);
          } else {
            await createEntryMutation.mutateAsync(entryData);
          }
          navigate(returnUrl);
        } else if (mode === 'approved') {
          await updateApprovedNotesMutation.mutateAsync({
            id: entryData.id,
            notes: entryData.notes,
            billingInformation: entryData.billingInformation,
            sendToCustomer: entryData.sendToCustomer ?? false,
          });
          navigate(returnUrl);
        } else if (mode === 'edit') {
          await updateEntryMutation.mutateAsync(entryData);
          navigate(returnUrl);
        }
      } catch (error) {
        // Error is handled by mutation's onError
        console.error('Save failed:', error);
      }
    },
    [
      mode,
      navigate,
      returnUrl,
      createEntryMutation,
      createMixtureMutation,
      updateEntryMutation,
      updateApprovedNotesMutation,
    ]
  );

  // Delete handler
  const deleteEntry = useCallback(async () => {
    if (!entry) return;

    try {
      await deleteEntryMutation.mutateAsync({
        id: entry.id,
        isProductMixture: entry.entryType === EntryType.Mixture,
      });
      navigate(returnUrl);
    } catch (error) {
      // Error is handled by mutation's onError
      console.error('Delete failed:', error);
    }
  }, [entry, deleteEntryMutation, navigate, returnUrl]);

  // Navigation handlers
  const close = useCallback(() => {
    navigate(returnUrl);
  }, [navigate, returnUrl]);

  const editNextUnconfirmed = useCallback(() => {
    if (nextUnconfirmed) {
      // Preserve return state when navigating to next entry
      navigate(`/bookkeeping/entry/${nextUnconfirmed.id}`, {
        state: { returnSearch },
      });
    }
  }, [nextUnconfirmed, navigate, returnSearch]);

  return {
    entry,
    isLoading,
    isError,
    mode,
    saveEntry,
    deleteEntry,
    isSaving,
    isDeleting,
    close,
    editNextUnconfirmed,
    hasNextUnconfirmed: !!nextUnconfirmed,
  };
};
